import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Mail, CheckCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export const EmailVerificationBanner = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isResending, setIsResending] = useState(false);

  // Check if email is verified
  const isVerified = user?.email_confirmed_at != null;

  const handleResendVerification = async () => {
    if (!user?.email) return;
    
    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: user.email,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });

      if (error) throw error;
      toast.success(t("dashboard.emailVerification.resent"));
    } catch (error) {
      console.error("Failed to resend verification:", error);
      toast.error(t("dashboard.emailVerification.resendFailed"));
    } finally {
      setIsResending(false);
    }
  };

  if (!user) return null;

  if (isVerified) {
    // Show verified status (optional: auto-hide after a few seconds)
    return (
      <div className="bg-[#6FEDD6] border-2 border-black rounded-xl px-4 py-3 mb-6 flex items-center gap-3">
        <CheckCircle className="w-5 h-5 text-black" />
        <span className="font-medium text-black">
          {t("dashboard.emailVerification.verified")}
        </span>
      </div>
    );
  }

  // Show pending verification
  return (
    <div className="bg-[#FFD93D] border-2 border-black rounded-xl px-4 py-3 mb-6 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Mail className="w-5 h-5 text-black" />
        <span className="font-medium text-black">
          {t("dashboard.emailVerification.pending")}
        </span>
      </div>
      <button
        onClick={handleResendVerification}
        disabled={isResending}
        className="
          flex items-center gap-2 px-3 py-1.5 
          bg-white border-2 border-black rounded-lg
          text-sm font-medium
          hover:translate-x-0.5 hover:translate-y-0.5
          hover:shadow-[2px_2px_0_#1A1A1A]
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-all duration-100
        "
      >
        <RefreshCw className={`w-4 h-4 ${isResending ? "animate-spin" : ""}`} />
        {t("dashboard.emailVerification.resend")}
      </button>
    </div>
  );
};
